import type { User as FirebaseUser } from 'firebase/auth';
import { onAuthStateChanged, signInWithPopup, signOut as firebaseSignOut, GoogleAuthProvider } from 'firebase/auth';
import { createContext, useContext, useEffect, useRef, useState, useCallback, type ReactNode } from 'react';
import { firebaseAuth } from '@/lib/firebase';
import { getImpersonationToken, clearImpersonationToken, isImpersonating as checkImpersonating, apiFetch } from '@/lib/api';
import i18n, { resolveLocale, LOCALE_STORAGE_KEY } from '@/i18n';

// ── Types ─────────────────────────────────────────────

export interface TenantUser {
  id: string;
  tenantId: string;
  email: string;
  name: string | null;
  role: 'ADMIN' | 'OPERATOR' | 'VIEWER' | 'DEVELOPER';
  preferredLocale: string | null;
  /**
   * If true this account is enska.ai staff — MUST NOT be allowed in tenant portal.
   * Redirect to admin.enska.ai instead.
   */
  isSuperAdminTenant: boolean;
  tenant: {
    id: string;
    name: string;
    slug: string;
    plan: string;
    status: string;
    defaultLocale: string;
  };
}

interface AuthState {
  firebaseUser:  FirebaseUser | null;
  platformUser:  TenantUser | null;
  idToken:       string | null;
  loading:       boolean;
  /** 'superadmin' means account is staff — blocked from this portal */
  error:         string | null;
  isImpersonating: boolean;
}

interface AuthContextValue extends AuthState {
  signInWithGoogle: () => Promise<void>;
  signOut:          () => Promise<void>;
  refreshToken:     () => Promise<string | null>;
  endImpersonation: () => void;
  /** Instantly switch UI language and persist preference to API */
  setLanguage:      (code: string) => Promise<void>;
}

// ── Context ───────────────────────────────────────────

const AuthContext = createContext<AuthContextValue | null>(null);

const ADMIN_PORTAL_URL = import.meta.env.VITE_ADMIN_URL ?? 'http://localhost:3000';

// ── Provider ──────────────────────────────────────────

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    firebaseUser: null,
    platformUser: null,
    idToken:      null,
    loading:      true,
    error:        null,
    isImpersonating: false,
  });
  const blockNextNullCallbackRef = useRef(false);

  // ── Handle impersonation token (injected by /auth/impersonate page) ────
  useEffect(() => {
    const impToken = getImpersonationToken();
    if (!impToken) return;
    // Use the dedicated impersonation endpoint — /v1/me requires Firebase auth
    const API_BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:3001';
    fetch(`${API_BASE}/v1/me/impersonate`, { headers: { Authorization: `Bearer ${impToken}` } })
      .then((r) => r.ok ? r.json() : null)
      .then((platformUser) => {
        if (!platformUser) {
          // Token is stale/expired — clear it so normal Firebase auth can proceed
          clearImpersonationToken();
          setState((s) => ({ ...s, loading: true, isImpersonating: false }));
          return;
        }
        setState({ firebaseUser: null, platformUser, idToken: impToken, loading: false, error: null, isImpersonating: true });
      })
      .catch(() => {
        clearImpersonationToken();
        setState((s) => ({ ...s, loading: true, isImpersonating: false }));
      });
  }, []);

  async function fetchPlatformUser(token: string): Promise<TenantUser | null> {
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL ?? 'http://localhost:3001'}/v1/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'x-enska-portal': 'tenant',
        },
      });
      if (!res.ok) return null;
      return res.json();
    } catch {
      return null;
    }
  }

  useEffect(() => {
    // NOTE: We do NOT skip this effect for impersonation sessions.
    // The impersonation useEffect clears stale tokens and updates state;
    // this listener co-exists safely — if impersonation resolved first,
    // the loading=false state prevents double-processing.

    const unsubscribe = onAuthStateChanged(firebaseAuth, async (fbUser) => {
      if (!fbUser) {
        if (blockNextNullCallbackRef.current) {
          blockNextNullCallbackRef.current = false;
          return;
        }
        // Don't wipe state if a valid impersonation session is active
        if (checkImpersonating()) return;
        setState({ firebaseUser: null, platformUser: null, idToken: null, loading: false, error: null, isImpersonating: false });
        return;
      }

      try {
        const token = await fbUser.getIdToken();
        const platformUser = await fetchPlatformUser(token);

        // ── SECURITY GUARD ─────────────────────────────
        // SuperAdmin accounts (enska.ai staff) must NOT use the tenant portal.
        // Redirect them to the admin portal immediately and sign out here.
        if (platformUser?.isSuperAdminTenant) {
          await firebaseSignOut(firebaseAuth);
          setState({
            firebaseUser: null,
            platformUser: null,
            idToken: null,
            loading: false,
            error: 'superadmin',
            isImpersonating: false,
          });
          // Hard redirect after a brief moment so the error message renders
          setTimeout(() => {
            window.location.href = `${ADMIN_PORTAL_URL}?redirected_from=portal`;
          }, 2000);
          return;
        }
        // ── END SECURITY GUARD ─────────────────────────

        // Block access — no platform account for this Google identity
        if (!platformUser) {
          blockNextNullCallbackRef.current = true;
          await firebaseSignOut(firebaseAuth);
          setState({ firebaseUser: null, platformUser: null, idToken: null, loading: false,
            error: 'No account found for this email. Contact your administrator.', isImpersonating: false });
          return;
        }

        // ── i18n: resolve and apply language ───────────
        const lang = resolveLocale(
          (platformUser as TenantUser | null)?.preferredLocale,
          (platformUser as TenantUser | null)?.tenant?.defaultLocale,
        );
        localStorage.setItem(LOCALE_STORAGE_KEY, lang);
        void i18n.changeLanguage(lang);
        // ── end i18n ───────────────────────────────────

        setState({ firebaseUser: fbUser, platformUser, idToken: token, loading: false, error: null, isImpersonating: false });
      } catch (err) {
        setState((s) => ({ ...s, loading: false, error: String(err) }));
      }
    });

    return unsubscribe;
  }, []);

  async function signInWithGoogle() {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(firebaseAuth, provider);
    } catch {
      setState((s) => ({ ...s, loading: false, error: 'Sign-in failed. Please try again.' }));
    }
  }

  async function signOut() {
    if (state.isImpersonating) {
      clearImpersonationToken();
      setState({ firebaseUser: null, platformUser: null, idToken: null, loading: false, error: null, isImpersonating: false });
      return;
    }
    await firebaseSignOut(firebaseAuth);
    setState({ firebaseUser: null, platformUser: null, idToken: null, loading: false, error: null, isImpersonating: false });
  }

  function endImpersonation() {
    clearImpersonationToken();
    window.location.href = `${ADMIN_PORTAL_URL}/tenants`;
  }

  async function refreshToken(): Promise<string | null> {
    if (!state.firebaseUser) return null;
    const token = await state.firebaseUser.getIdToken(true);
    setState((s) => ({ ...s, idToken: token }));
    return token;
  }

  /** Instantly switch UI language, save to localStorage, and persist to API */
  const setLanguage = useCallback(async (code: string) => {
    localStorage.setItem(LOCALE_STORAGE_KEY, code);
    await i18n.changeLanguage(code);
    // Best-effort persist to API — don't block UI on failure
    try {
      await apiFetch('/v1/me/locale', { method: 'PATCH', body: JSON.stringify({ locale: code }) });
    } catch {
      // non-fatal — preference is already saved in localStorage
    }
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, signInWithGoogle, signOut, refreshToken, endImpersonation, setLanguage }}>
      {children}
    </AuthContext.Provider>
  );
}

// ── Hook ──────────────────────────────────────────────

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
